import { Box, ButtonBase } from "@mui/material";
import { colorPalette } from "../../config/colorPalette";

const textSx = {
  fontFamily: '"Lexend", sans-serif',
  fontSize: 16,
  fontStyle: "normal",
  lineHeight: "24px",
  letterSpacing: "0.2px",
};

/**
 * Breadcrumb

 * Props:
 * - items: [{ label, onClick? }]. The last item is the current page (medium weight,
 *   full colour). Earlier items with onClick render as buttons.
 * - separator: node between items (default "/")
 *
 * A long current page name is cut with an ellipsis (its full text is the hover title) instead of
 * pushing what is beside the breadcrumb out of the way; the earlier crumbs never shrink.
 * - sx: extra styles, merged last
 */
const Breadcrumb = ({
  items = [],
  separator = "/",
  sx: sxOverride = {},
  ...rest
}) => (
  <Box
    component="nav"
    aria-label="Breadcrumb"
    {...rest}
    sx={{ padding: "12px 16px", ...sxOverride }}
  >
    <Box
      component="ol"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        margin: 0,
        padding: 0,
        listStyle: "none",
        minWidth: 0,
      }}
    >
      {items.map((item, index) => {
        const isCurrent = index === items.length - 1;
        const label = isCurrent ? (
          <Box
            component="span"
            aria-current="page"
            title={typeof item.label === "string" ? item.label : undefined}
            sx={{
              ...textSx,
              fontWeight: 500,
              color: colorPalette.mercury[950],
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
          </Box>
        ) : (
          <Box
            component={item.onClick ? ButtonBase : "span"}
            onClick={item.onClick}
            sx={{
              ...textSx,
              fontWeight: 400,
              color: colorPalette.mercuryAlpha[55],
            }}
          >
            {item.label}
          </Box>
        );

        return (
          <Box
            component="li"
            key={`${item.label}-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              // The current page may shrink; the earlier crumbs keep their size.
              minWidth: isCurrent ? 0 : undefined,
              flexShrink: isCurrent ? 1 : 0,
              whiteSpace: "nowrap",
            }}
          >
            {label}
            {!isCurrent && (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  ...textSx,
                  fontWeight: 400,
                  color: colorPalette.mercuryAlpha[45],
                }}
              >
                {separator}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default Breadcrumb;
